import React, { useState, useEffect } from "react"

import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import BackgroundColorToggle from "../shared/BackgroundColorToggle"

// ColorInput component (not currently used but kept for future use)
// const ColorInput = ({ label, value, onChange, className = "" }) => (
//   <div className={`space-y-1 ${className}`}>
//     <label className="block text-sm font-medium text-gray-700">{label}</label>
//     <div className="flex gap-2">
//       <input
//         type="color"
//         value={value || "#ffffff"}
//         onChange={(e) => onChange(e.target.value)}
//         className="h-8 w-12 cursor-pointer rounded border border-gray-light"
//       />
//       <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="#ffffff" className="flex-1" />
//     </div>
//   </div>
// )

const ServicesEditor = ({ section, onSave, onCancel }) => {
  const { handleUpdateSectionPreview, clearPreview, practice } = useWebsiteBuilderV2()
  const [formData, setFormData] = useState({
    headline: section?.headline || "Services",
    body: section?.body || "",
    settings: section?.settings || {
      layout: "list", // list, highlights_2, highlights_3
      showBackgroundColor: false,
      backgroundColor: "#ffffff",
      selectedServiceIds: []
    }
  })

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState(null)

  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (section) {
      // Use section headline if it exists and is valid, otherwise default to "Services"
      // Fix common issues: if headline is "Featured services" but layout is "services", use "Services"
      let defaultHeadline = section.headline || "Services"
      if (
        section.layout === "services" &&
        (defaultHeadline.toLowerCase().includes("featured") || defaultHeadline === "featured_services")
      ) {
        defaultHeadline = "Services"
      }
      setFormData({
        headline: defaultHeadline,
        body: section.body || "",
        settings: {
          layout: "list",
          showBackgroundColor: false,
          backgroundColor: "#ffffff",
          selectedServiceIds: [],
          ...section.settings
        }
      })
    }
  }, [section])

  // Get available public services
  const availableServices = (practice?.services || []).filter((service) => service.public === true)

  // Get selected services in order
  const selectedServices = (formData.settings.selectedServiceIds || [])
    .map((id) => availableServices.find((service) => service.id === id))
    .filter(Boolean)

  // Get unselected services
  const unselectedServices = availableServices.filter(
    (service) => !(formData.settings.selectedServiceIds || []).includes(service.id)
  )

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }

    // Update preview immediately for content changes
    if (section?.id && ["headline", "subheadline", "body"].includes(field)) {
      handleUpdateSectionPreview(section.id, {
        ...section,
        ...formData,
        [field]: value
      })
    }
  }

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => {
      const newSettings = { ...prev.settings, [key]: value }

      // Immediately update preview for important settings (avoids useEffect loops)
      const importantSettings = ["layout", "showBackgroundColor", "backgroundColor", "selectedServiceIds"]
      if (section?.id && importantSettings.includes(key)) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          ...prev,
          settings: newSettings
        })
      }

      return {
        ...prev,
        settings: newSettings
      }
    })
  }

  // Service selection handlers
  const handleAddService = (serviceId) => {
    const currentIds = formData.settings.selectedServiceIds || []
    const newSelectedIds = [...currentIds, serviceId]
    handleSettingsChange("selectedServiceIds", newSelectedIds)
  }

  const handleRemoveService = (serviceId) => {
    const currentIds = formData.settings.selectedServiceIds || []
    const newSelectedIds = currentIds.filter((id) => id !== serviceId)
    handleSettingsChange("selectedServiceIds", newSelectedIds)
  }

  const handleSelectAllServices = () => {
    const allServiceIds = availableServices.map((s) => s.id)
    handleSettingsChange("selectedServiceIds", allServiceIds)
  }

  const handleClearServices = () => {
    handleSettingsChange("selectedServiceIds", [])
  }

  // Drag and drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === index) return

    const currentIds = formData.settings.selectedServiceIds || []
    const newSelectedIds = [...currentIds]
    const draggedId = newSelectedIds[draggedIndex]
    newSelectedIds.splice(draggedIndex, 1)
    newSelectedIds.splice(index, 0, draggedId)

    handleSettingsChange("selectedServiceIds", newSelectedIds)
    setDraggedIndex(index)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.headline.trim()) {
      newErrors.headline = "Section headline is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (validateForm()) {
      onSave({
        layout: "services",
        ...formData
      })
    }
  }

  // Layout options with preview images - updated to match Figma design
  const layoutOptions = [
    {
      id: "list",
      name: "List",
      description: "Vertical list of services",
      image: require("../../../../../assets/images/builder/v2/layout-images/list.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/list-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/list-selected.png")
    },
    {
      id: "highlights_2",
      name: "2 Highlights",
      description: "Two highlighted services in a grid",
      image: require("../../../../../assets/images/builder/v2/layout-images/2-highlights.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/2-highlights-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/2-highlights-selected.png")
    },
    {
      id: "highlights_3",
      name: "3 Highlights",
      description: "Three highlighted services in a grid",
      image: require("../../../../../assets/images/builder/v2/layout-images/3-highlights.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/3-highlights-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/3-highlights-selected.png")
    }
  ]

  const LayoutOption = ({ option, isSelected, onSelect }) => {
    const [isHovered, setIsHovered] = useState(false)

    const getImageSource = () => {
      if (isSelected) {
        return option.imageSelected
      } else if (isHovered) {
        return option.imageHover
      } else {
        return option.image
      }
    }

    return (
      <div
        className="cursor-pointer transition-all"
        onClick={() => onSelect(option.id)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title={option.name}>
        <img
          src={getImageSource()}
          alt={`${option.name} layout`}
          className="h-16 w-24 rounded-lg object-cover transition-all duration-200"
          onError={(e) => {
            console.warn(`Failed to load image: ${e.target.src}`) // eslint-disable-line no-console
            e.target.style.display = "none"
            e.target.nextSibling.style.display = "block"
          }}
        />
        {/* Fallback placeholder */}
        <div className="hidden h-16 w-24 items-center justify-center rounded-lg bg-gray-200">
          <div className="text-xs text-gray-500">{option.name}</div>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Form Content */}
      <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-8 pt-6">
        {/* Layout Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Layout</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Select a layout design for this section:</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 content-start items-start justify-start gap-3 text-clip p-0 [flex-flow:wrap]">
            {layoutOptions.map((option) => (
              <LayoutOption
                key={option.id}
                option={option}
                isSelected={formData.settings.layout === option.id}
                onSelect={(layoutId) => handleSettingsChange("layout", layoutId)}
              />
            ))}
          </div>
        </div>

        {/* Services Selection Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Services to Display</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">
                Choose which services to display and drag to reorder them. Leave empty to show all services.
              </p>
            </div>
          </div>

          {/* Selected Services */}
          {selectedServices.length > 0 && (
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
              <div className="flex items-center justify-between">
                <div className="relative shrink-0 font-['Manrope:Bold',_sans-serif] text-[14px] font-semibold text-[#0b3954]">
                  Selected Services ({selectedServices.length})
                </div>
                <button
                  type="button"
                  onClick={handleClearServices}
                  className="text-[13px] font-medium text-[#bd654f] hover:text-[#a55a45]">
                  Clear All
                </button>
              </div>
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 rounded-lg border border-[#c8d6df] bg-[#f8fbfd] p-3">
                {selectedServices.map((service, index) => (
                  <div
                    key={service.id}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`relative box-border flex w-full cursor-move flex-row content-stretch items-center justify-between gap-3 rounded-lg border bg-white p-3 transition-all hover:shadow-sm ${
                      draggedIndex === index ? "border-[#0d9da4] opacity-50" : "border-[#e4eff6]"
                    }`}>
                    <div className="flex flex-1 items-center gap-3">
                      <div className="flex h-6 w-6 items-center justify-center">
                        <svg className="h-4 w-4 text-[#55778a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                        </svg>
                      </div>
                      <div className="flex flex-1 flex-col gap-1">
                        <div className="font-['Manrope:Bold',_sans-serif] text-[14px] font-semibold text-[#0b3954]">
                          {service.name}
                        </div>
                        {service.amountCents > 0 && (
                          <div className="font-['Manrope:Regular',_sans-serif] text-[12px] text-[#55778a]">
                            ${(service.amountCents / 100).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveService(service.id)}
                      className="flex h-6 w-6 items-center justify-center rounded text-[#bd654f] hover:bg-red-50">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Available Services */}
          {unselectedServices.length > 0 && (
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
              <div className="flex items-center justify-between">
                <div className="relative shrink-0 font-['Manrope:Bold',_sans-serif] text-[14px] font-semibold text-[#0b3954]">
                  Available Services ({unselectedServices.length})
                </div>
                <button
                  type="button"
                  onClick={handleSelectAllServices}
                  className="text-[13px] font-medium text-[#0d9da4] hover:text-[#0b8a90]">
                  Select All
                </button>
              </div>
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 rounded-lg border border-[#c8d6df] bg-white p-3">
                {unselectedServices.map((service) => (
                  <div
                    key={service.id}
                    className="relative box-border flex w-full flex-row content-stretch items-center justify-between gap-3 rounded-lg border border-[#e4eff6] bg-[#f8fbfd] p-3 transition-all hover:border-[#c8d6df] hover:bg-white">
                    <div className="flex flex-1 flex-col gap-1">
                      <div className="font-['Manrope:Bold',_sans-serif] text-[14px] font-semibold text-[#0b3954]">
                        {service.name}
                      </div>
                      {service.amountCents > 0 && (
                        <div className="font-['Manrope:Regular',_sans-serif] text-[12px] text-[#55778a]">
                          ${(service.amountCents / 100).toFixed(2)}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAddService(service.id)}
                      className="flex h-8 items-center justify-center rounded bg-[#0d9da4] px-3 text-[13px] font-medium text-white transition-colors hover:bg-[#0b8a90]">
                      Add
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No services available message */}
          {availableServices.length === 0 && (
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-center justify-center gap-2 rounded-lg border border-[#c8d6df] bg-[#f8fbfd] p-6 text-center">
              <div className="font-['Manrope:Bold',_sans-serif] text-[14px] font-semibold text-[#55778a]">
                No public services available
              </div>
              <div className="font-['Manrope:Regular',_sans-serif] text-[13px] text-[#55778a]">
                Create and publish services to display them here.
              </div>
            </div>
          )}
        </div>

        {/* Title Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Title</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">
                Create a powerful headline to capture attention and communicate your core message.
              </p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative w-full shrink-0 rounded-lg bg-white">
              <input
                type="text"
                value={formData.headline}
                onChange={(e) => handleChange("headline", e.target.value)}
                placeholder="Services"
                className="relative box-border flex w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
                required
              />
            </div>
            {errors.headline && (
              <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-red-500">
                <p className="block leading-[19.5px]">{errors.headline}</p>
              </div>
            )}
          </div>
        </div>

        {/* Description Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Description</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">
                Add a brief description to provide more context about your services.
              </p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative w-full shrink-0 rounded-lg bg-white">
              <textarea
                value={formData.body}
                onChange={(e) => handleChange("body", e.target.value)}
                placeholder="Tell visitors about your services..."
                rows={3}
                className="relative box-border flex w-full resize-none flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Background Color Section */}
        <BackgroundColorToggle formData={formData} onSettingsChange={handleSettingsChange} sectionType="services" />
      </div>

      {/* Footer Buttons */}
      <div className="box-border flex w-full flex-row content-stretch items-center justify-between border-t border-[#e4eff6] bg-white p-[32px]">
        <div className="relative box-border flex min-h-px min-w-px shrink-0 grow basis-0 flex-row content-stretch items-center justify-end gap-4 p-0">
          <button
            onClick={() => {
              clearPreview() // Clear any preview changes
              onCancel()
            }}
            className="relative box-border flex shrink-0 cursor-pointer flex-row content-stretch items-center justify-center gap-2 overflow-visible rounded border border-[#c8d6df] bg-white px-4 py-0 transition-colors hover:bg-gray-50">
            <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
              <p className="block whitespace-pre leading-[32px]">Close</p>
            </div>
          </button>
          <button
            onClick={handleSubmit}
            className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-center gap-2 rounded bg-[#0d9da4] px-4 py-0 transition-colors hover:bg-[#0b8a90]">
            <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-white">
              <p className="block whitespace-pre leading-[32px]">Save</p>
            </div>
          </button>
        </div>
      </div>
    </>
  )
}

export default ServicesEditor
